// frontend/src/pages/dashboards/AddToDashboardDialog.jsx
// Phase 4 step 14a-3: "Add to dashboard" on a chart's page. Lists the
// organization's dashboards; picking one fetches it, places a tile for this
// chart in its next free spot (gridLayout.addTile) and saves against the
// dashboard's current revision, so a dashboard being edited elsewhere is
// never overwritten (the server refuses with DASHBOARD_CHANGED and this says
// so). Admins and analysts only (the server enforces it).
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Dialog } from 'radix-ui';
import { Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { getDashboard, listDashboards, saveDashboard } from '../../charts/semanticApi';
import { MAX_TILES, addTile, readingOrder } from './gridLayout';
import { draftFromServer, newChartTile, tileCountText, tileToSave } from './dashboardState';

/** The PUT body that appends `chart` to a server dashboard, or null when it is full. Pure. */
export function appendChartBody(dashboard, chart) {
  const draft = draftFromServer(dashboard);
  const next = addTile(draft.tiles, newChartTile(chart));
  if (!next) return null;
  return { revision: dashboard.revision, tiles: readingOrder(next).map(tileToSave) };
}

export function addFailureMessage(err) {
  const code = err && err.code;
  if (code === 'DASHBOARD_CHANGED') return 'That dashboard was just changed by someone else. Try again.';
  if (code === 'CHART_NOT_FOUND') return 'This chart could not be added. Reload the page and try again.';
  if (code === 'FORBIDDEN' || (err && err.status === 403)) return "You don't have permission to change dashboards.";
  if (code === 'NOT_FOUND' || (err && err.status === 404)) return 'That dashboard no longer exists.';
  return "The chart couldn't be added. Try again.";
}

/** open, onClose, chart: the saved chart (GET /charts/{id}), onAdded(dashboard). */
export default function AddToDashboardDialog({ open, onClose, chart, onAdded }) {
  const [state, setState] = useState({ status: 'idle', dashboards: [], error: null });
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  const [addedTo, setAddedTo] = useState(null);
  const firstRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const ctrl = new AbortController();
    setError(''); setAddedTo(null); setBusyId(null);
    setState({ status: 'loading', dashboards: [], error: null });
    listDashboards({ signal: ctrl.signal })
      .then((body) => { if (!ctrl.signal.aborted) setState({ status: 'ready', dashboards: body.dashboards || [], error: null }); })
      .catch((err) => { if (!ctrl.signal.aborted) setState({ status: 'error', dashboards: [], error: err }); });
    return () => ctrl.abort();
  }, [open]);

  const add = async (d) => {
    if (busyId) return;
    setBusyId(d.id); setError('');
    try {
      const full = await getDashboard(d.id);
      const body = appendChartBody(full, chart);
      if (!body) { setError(`"${d.title}" already holds ${MAX_TILES} tiles.`); setBusyId(null); return; }
      const saved = await saveDashboard(d.id, body);
      setAddedTo(saved);
      setBusyId(null);
      if (onAdded) onAdded(saved);
    } catch (err) {
      setError(addFailureMessage(err));
      setBusyId(null);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(next) => { if (!next && !busyId) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content
          onOpenAutoFocus={(e) => { if (firstRef.current) { e.preventDefault(); firstRef.current.focus(); } }}
          className="fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-[22px] bg-[var(--ui-panel)] p-6 text-[var(--ui-text)] shadow-[0_24px_60px_rgba(0,0,0,0.6)] ring-1 ring-white/5 focus:outline-none"
        >
          <Dialog.Title className="mb-1 text-lg font-semibold tracking-[-0.01em]">Add to a dashboard</Dialog.Title>
          <Dialog.Description className="mb-4 text-sm text-[var(--ui-text-muted)]">
            {addedTo ? `Added to "${addedTo.title}".` : `"${chart ? chart.title : 'This chart'}" is placed in the next free spot; you can move it there.`}
          </Dialog.Description>
          <div className="max-h-[50vh] min-h-[80px] overflow-y-auto rounded-xl bg-[var(--ui-inset)] scrollbar-thin">
            {state.status === 'loading' && (
              <p className="flex items-center justify-center gap-2 py-8 text-sm text-[var(--ui-text-muted)]" role="status">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading dashboards
              </p>
            )}
            {state.status === 'error' && <p className="py-8 text-center text-sm text-[var(--ui-text-muted)]">The dashboards couldn't be loaded.</p>}
            {state.status === 'ready' && state.dashboards.length === 0 && (
              <p className="px-4 py-8 text-center text-sm text-[var(--ui-text-muted)]">No dashboards yet. Make one on the Dashboards page first.</p>
            )}
            {state.status === 'ready' && state.dashboards.map((d, i) => {
              const done = addedTo && addedTo.id === d.id;
              return (
                <button key={d.id} type="button" ref={i === 0 ? firstRef : undefined} onClick={() => add(d)} disabled={Boolean(busyId) || Boolean(addedTo)}
                  className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-[13px] hover:bg-[var(--ui-inset-hover)] focus:outline-none focus-visible:bg-[var(--ui-inset-hover)] disabled:cursor-default disabled:hover:bg-transparent">
                  <span className="min-w-0">
                    <span className="block truncate">{d.title}</span>
                    <span className="block text-xs text-[var(--ui-text-muted)]">{tileCountText(d.tile_count)}</span>
                  </span>
                  <span className="shrink-0 text-xs text-[var(--ui-text-muted)]">
                    {busyId === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : done ? <Check className="h-3.5 w-3.5 text-emerald-500" aria-label="Added" /> : addedTo ? '' : 'Add'}
                  </span>
                </button>
              );
            })}
          </div>
          {error && <p className="mt-2 text-xs text-red-400" role="alert">{error}</p>}
          <div className="mt-4 flex items-center justify-end gap-3">
            {addedTo && <Button asChild size="sm"><Link to={`/dashboards/${addedTo.id}`}>Open the dashboard</Link></Button>}
            <Button variant="secondary" size="sm" onClick={onClose} disabled={Boolean(busyId)}>{addedTo ? 'Done' : 'Cancel'}</Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
